import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { rupees, fmtDate } from "@/lib/helpers";
import { StatusBadge, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { SignOut, UsersThree, BookOpen, HandCoins, ChartBar, Receipt } from "@phosphor-icons/react";

const SOURCE_LABEL = { pos: "POS Sale", referral: "Referral Sale", scheme: "Scheme Installment" };

export default function AgentPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/agent-portal/me").then(({ data }) => setD(data)).catch(() => {}); }, []);

  const openReceipt = async (sid) => {
    const token = localStorage.getItem("og_token");
    const r = await fetch(`${API}/agent-portal/settlements/${sid}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) window.open(URL.createObjectURL(await r.blob()), "_blank"); else toast.error("Could not open receipt");
  };

  const kpi = (label, value, accent) => (
    <Card className="p-4 bg-white border-[#E6E2D6] shadow-sm" data-testid={`agent-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">{label}</div>
      <div className={`text-xl font-medium tabular-nums mt-1 ${accent || "text-[#1A1A1A]"}`}>{value}</div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="h-16 bg-white/95 backdrop-blur border-b border-[#E6E2D6] flex items-center justify-between px-6 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="font-display text-2xl font-semibold text-[#C5A059] tracking-wide">OM GOLD</div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">Agent Portal</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#4A4A4A]">{d?.agent?.name || user?.name} {d?.agent?.agent_code ? `· ${d.agent.agent_code}` : ""}</div>
          <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="agent-portal-logout"><SignOut size={18} /></Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 fade-in">
        {!d ? <div className="text-[#8A8A8A] py-12 text-center">Loading your earnings…</div> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {kpi("Total Earned", rupees(d.summary.total_commission), "text-[#8A6D2F]")}
              {kpi("Paid (Settled)", rupees(d.summary.paid), "text-[#2D6A4F]")}
              {kpi("Pending", rupees(d.summary.pending), "text-[#B45309]")}
              {kpi("Customers Referred", d.summary.customers_referred)}
            </div>

            <Tabs defaultValue="referred">
              <TabsList className="bg-[#F2EBE1]">
                <TabsTrigger value="referred" data-testid="agent-tab-referred"><UsersThree size={15} className="mr-1" /> Referred Customers</TabsTrigger>
                <TabsTrigger value="ledger" data-testid="agent-tab-ledger"><BookOpen size={15} className="mr-1" /> Commission Ledger</TabsTrigger>
                <TabsTrigger value="settlements" data-testid="agent-tab-settlements"><HandCoins size={15} className="mr-1" /> Settlements</TabsTrigger>
                <TabsTrigger value="earnings" data-testid="agent-tab-earnings"><ChartBar size={15} className="mr-1" /> Earnings</TabsTrigger>
              </TabsList>

              {/* Referred customers with per-customer purchase/scheme detail */}
              <TabsContent value="referred">
                {d.referred_customers.length === 0 ? (
                  <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3"><EmptyState label="No referred customers yet" /></Card>
                ) : (
                  <div className="space-y-3 mt-3">
                    {d.referred_customers.map((c) => (
                      <Card key={c.customer_id} className="bg-white border-[#E6E2D6] shadow-sm overflow-hidden" data-testid={`referred-${c.customer_id}`}>
                        <div className="flex items-center justify-between px-4 py-3 bg-[#F8F6F0] border-b border-[#E6E2D6]">
                          <div className="font-medium text-[#1A1A1A]">{c.customer_name}</div>
                          <div className="text-right">
                            <span className="text-[10px] uppercase tracking-wider text-[#8A8A8A] mr-2">My Earnings</span>
                            <span className="text-base font-medium tabular-nums text-[#8A6D2F]">{rupees(c.total_commission)}</span>
                          </div>
                        </div>
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead>
                            <TableHead className="text-right">Eligible</TableHead><TableHead className="text-right">My Commission</TableHead><TableHead>Status</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>{c.items.map((it, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-sm">{fmtDate(it.date)}</TableCell>
                              <TableCell className="text-sm">{SOURCE_LABEL[it.source] || it.source}</TableCell>
                              <TableCell className="text-sm">{it.reference || "-"}</TableCell>
                              <TableCell className="text-right tabular-nums text-sm">{rupees(it.eligible_amount)}</TableCell>
                              <TableCell className="text-right tabular-nums">{rupees(it.commission_amount)}</TableCell>
                              <TableCell><StatusBadge status={it.status} /></TableCell>
                            </TableRow>
                          ))}</TableBody>
                        </Table>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Full commission ledger with detailed statuses */}
              <TabsContent value="ledger">
                <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
                  {d.commissions.length === 0 ? <EmptyState label="No commission entries yet" /> : (
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead>
                        <TableHead className="text-right">Eligible</TableHead><TableHead className="text-right">Commission</TableHead><TableHead>Status</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>{d.commissions.map((c) => (
                        <TableRow key={c.id} data-testid={`agent-comm-${c.id}`}>
                          <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
                          <TableCell className="text-sm">{c.customer_name || "-"}</TableCell>
                          <TableCell className="text-sm">{SOURCE_LABEL[c.source] || c.source}</TableCell>
                          <TableCell className="text-sm">{c.invoice_no || "-"}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{rupees(c.eligible_amount)}</TableCell>
                          <TableCell className="text-right tabular-nums">{rupees(c.commission_amount)}</TableCell>
                          <TableCell><StatusBadge status={c.status} /></TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>

              {/* Settlements (paid) with receipt download */}
              <TabsContent value="settlements">
                <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
                  {d.settlements.length === 0 ? <EmptyState label="No settlements yet" /> : (
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Date</TableHead><TableHead>Method</TableHead><TableHead>Reference</TableHead>
                        <TableHead className="text-right">TDS</TableHead><TableHead className="text-right">Net Paid</TableHead><TableHead></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>{d.settlements.map((s) => (
                        <TableRow key={s.id} data-testid={`agent-settlement-${s.id}`}>
                          <TableCell className="text-sm">{fmtDate(s.created_at)}</TableCell>
                          <TableCell className="text-sm">{s.method}</TableCell>
                          <TableCell className="text-sm">{s.reference || "-"}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{rupees(s.tds || 0)}</TableCell>
                          <TableCell className="text-right tabular-nums text-[#2D6A4F]">{rupees(s.net_paid ?? s.amount)}</TableCell>
                          <TableCell><Button size="sm" variant="ghost" onClick={() => openReceipt(s.id)} data-testid={`agent-receipt-${s.id}`}><Receipt size={15} className="mr-1" /> Receipt</Button></TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>

              {/* Monthly earnings chart */}
              <TabsContent value="earnings">
                <Card className="p-4 bg-white border-[#E6E2D6] shadow-sm mt-3">
                  <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Monthly Earnings</div>
                  {d.monthly.length === 0 ? <EmptyState label="No earnings to chart yet" /> : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={d.monthly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEE8DC" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => rupees(v)} />
                        <Bar dataKey="commission" fill="#C5A059" radius={[4, 4, 0, 0]} name="Commission" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
        <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-[#8A8A8A]">
          <img src="/microcore-logo.png" alt="MICROCORE" className="h-6 w-6 object-contain" />
          Developed by <a href="https://www.microcoreitsolutions.com/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] font-semibold hover:underline">MICROCORE IT SOLUTIONS</a>
        </div>
      </main>
    </div>
  );
}
